import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, unwrap, errMsg } from "@/lib/api";
import { displayPeriodLabel } from "@/lib/feeSelection";
import { useI18n } from "@/i18n";
import { TermBalanceCard } from "@/components/FeePlan";
import { useQuery, useQueries, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatMoney, formatDate, statusTone } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CircleDollarSign,
  Receipt,
  Loader2,
  History,
  ChevronDown,
  ChevronUp,
  Download,
} from "lucide-react";

const toneClass = (tone) =>
  ({
    success: "bg-[var(--success-bg)] text-[var(--success)]",
    warning: "bg-[var(--warning-bg)] text-[var(--warning)]",
    error: "bg-[var(--error-bg)] text-[var(--error)]",
    info: "bg-[var(--info-bg)] text-[var(--info)]",
  })[tone] || "bg-[var(--subtle)] text-[var(--text-secondary)]";

export default function GuardianFees() {
  const navigate = useNavigate();
  const { language, t } = useI18n();
  const localizedValue = (group, value) => t(`guardian.${group}.${value}`);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["invoices"],
    queryFn: async () => unwrap(await api.get("/invoices")),
  });
  // Term balance per child for the current academic year.
  const { data: home } = useQuery({
    queryKey: ["guardian-home"],
    queryFn: async () => unwrap(await api.get("/guardian/home")),
  });
  const { data: years } = useQuery({
    queryKey: ["ay"],
    queryFn: async () => unwrap(await api.get("/academic-years")),
  });
  const currentYearId = (years || []).find((y) => y.is_current)?.id;

  const initPayment = useMutation({
    mutationFn: async (invoiceId) =>
      unwrap(
        await api.post("/payments/init", {
          invoice_id: invoiceId,
          return_url: `${window.location.origin}/parent/fees/payment`,
        }),
      ),
    onSuccess: (d) => {
      setPayingInvoice(null);
      if (d.payment_url) window.location.href = d.payment_url;
    },
    onError: (e) => {
      setPayingInvoice(null);
      toast.error(errMsg(e));
    },
  });

  const detail = useQuery({
    queryKey: ["invoice-detail", historyOpen],
    queryFn: async () => unwrap(await api.get(`/invoices/${historyOpen}`)),
    enabled: !!historyOpen,
  });

  const downloadPdf = (inv) => {
    const token = localStorage.getItem("eduos_token");
    if (!token) return;
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/v1/fees/invoices/${inv.id}/pdf?lang=${language}`;
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.responseType = "blob";
    xhr.onload = () => {
      const blob = new Blob([xhr.response], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `invoice-${inv.invoice_no}.pdf`;
      a.click();
    };
    xhr.send();
  };

  // What is owed by the fee plan across the wards (term balance), once the
  // school tracks dues for the year; otherwise unpaid invoices.
  const wardSummaries = useQueries({
    queries: (home?.wards || []).map((ward) => ({
      queryKey: ["fee-summary", ward.student.id, currentYearId],
      queryFn: async () => unwrap(await api.get(`/students/${ward.student.id}/fee-summary`, { params: { academic_year_id: currentYearId } })),
      enabled: Boolean(currentYearId),
    })),
  });
  const trackedSummaries = wardSummaries.map((q) => q.data).filter((d) => d?.calendar_configured && (d.rows || []).length);
  const termBalance = trackedSummaries.length
    ? trackedSummaries.reduce((acc, d) => ({
        due: acc.due + (d.totals?.total_due_paise || 0),
        upcoming: acc.upcoming + (d.totals?.upcoming_paise || 0),
      }), { due: 0, upcoming: 0 })
    : null;

  if (isLoading) return <Skeleton className="h-96 w-full" />;
  const invoices = data || [];

  const invoiceDue = invoices.reduce((a, i) => a + (i.balance_paise || 0), 0);

  return (
    <div className="space-y-6" data-testid="guardian-fees">
      <div>
        <h1 className="font-display text-2xl font-bold text-[var(--text-primary)]">
          {t("guardian.feesTitle")}
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {t("guardian.feesSubtitle")}
        </p>
      </div>

      <Card className="border-[var(--subtle)] bg-gradient-to-br from-[var(--brand)] to-[var(--brand-hover)] text-white">
        <CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-white/60 font-semibold">
            {termBalance ? t("feePlan.totalDue") : t("guardian.totalOutstanding")}
          </div>
          <div className="font-mono-num text-4xl font-bold mt-1" data-testid="guardian-total-due">
            {formatMoney(termBalance ? termBalance.due : invoiceDue)}
          </div>
          <div className="text-xs text-white/60 mt-2">
            {termBalance
              ? `${t("feePlan.upcoming")} ${formatMoney(termBalance.upcoming)}`
              : t("guardian.unpaidOfTotal", {
                  unpaid: invoices.filter((i) => i.balance_paise > 0).length,
                  total: invoices.length,
                })}
          </div>
        </CardContent>
      </Card>

      {currentYearId && (home?.wards || []).map((ward) => (
        <TermBalanceCard
          key={ward.student.id}
          studentId={ward.student.id}
          academicYearId={currentYearId}
          staff={false}
          title={`${t("feePlan.termBalance")} · ${ward.student.first_name} ${ward.student.last_name}`}
        />
      ))}

      <div className="space-y-3 stagger">
        {invoices.map((inv) => (
          <Card
            key={inv.id}
            className="border-[var(--subtle)] lift-card"
            data-testid={`invoice-${inv.invoice_no}`}
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-[var(--text-muted)] font-mono-num">
                    {inv.invoice_no}
                  </div>
                  <div className="font-semibold text-[var(--text-primary)] mt-0.5">
                    {displayPeriodLabel(inv.period_label, language)} ·{" "}
                    {inv.student
                      ? `${inv.student.first_name} ${inv.student.last_name}`
                      : ""}
                  </div>
                  <div className="text-xs text-[var(--text-muted)] mt-1">
                    {t("guardian.dueOn", { date: formatDate(inv.due_date) })}
                  </div>
                </div>
                <Badge
                  className={`${toneClass(statusTone(inv.status))} border-0 capitalize`}
                >
                  {localizedValue("statuses", inv.status)}
                </Badge>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase text-[var(--text-muted)] tracking-widest">
                    {t("guardian.total")}
                  </div>
                  <div className="font-mono-num font-semibold">
                    {formatMoney(inv.total_paise)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[var(--text-muted)] tracking-widest">
                    {t("guardian.paid")}
                  </div>
                  <div className="font-mono-num font-semibold text-[var(--success)]">
                    {formatMoney(inv.paid_paise)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[var(--text-muted)] tracking-widest">
                    {t("guardian.balance")}
                  </div>
                  <div className="font-mono-num font-semibold text-[var(--accent)]">
                    {formatMoney(inv.balance_paise)}
                  </div>
                </div>
              </div>
              {inv.balance_paise > 0 && (
                <div className="mt-4">
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={initPayment.isPending && payingInvoice === inv.id}
                    onClick={() => {
                      setPayingInvoice(inv.id);
                      initPayment.mutate(inv.id);
                    }}
                  >
                    {initPayment.isPending && payingInvoice === inv.id ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />{" "}
                        {t("guardian.startingPayment")}
                      </>
                    ) : (
                      t("guardian.payOnline")
                    )}
                  </Button>
                </div>
              )}
              <div className="mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadPdf(inv)}
                  data-testid={`pdf-${inv.invoice_no}`}
                  className="h-8 w-full"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" />{" "}
                  {t("guardian.invoicePdf")}
                </Button>
              </div>
              <div className="mt-4 border-t border-[var(--subtle)] pt-3">
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--accent)] transition-colors"
                  data-testid={`history-toggle-${inv.invoice_no}`}
                  onClick={() =>
                    setHistoryOpen(historyOpen === inv.id ? null : inv.id)
                  }
                >
                  {historyOpen === inv.id ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                  <History className="h-3.5 w-3.5" />
                  {t("guardian.paymentHistory")}
                </button>
                {historyOpen === inv.id && (
                  <div
                    className="mt-3"
                    data-testid={`history-${inv.invoice_no}`}
                  >
                    {detail.isLoading ? (
                      <div className="text-xs text-[var(--text-muted)] flex items-center gap-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />{" "}
                        {t("guardian.loading")}
                      </div>
                    ) : (detail.data?.payments || []).length === 0 ? (
                      <div className="text-xs text-[var(--text-muted)]">
                        {t("guardian.noPaymentsYet")}
                      </div>
                    ) : (
                      <div className="overflow-hidden rounded border border-[var(--subtle)]">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-[var(--bg)] text-[var(--text-muted)] uppercase text-[10px] tracking-widest">
                              <th className="px-3 py-2 text-left font-semibold">
                                {t("guardian.date")}
                              </th>
                              <th className="px-3 py-2 text-left font-semibold">
                                {t("guardian.receipt")}
                              </th>
                              <th className="px-3 py-2 text-left font-semibold">
                                {t("guardian.method")}
                              </th>
                              <th className="px-3 py-2 text-right font-semibold">
                                {t("guardian.amount")}
                              </th>
                              <th className="px-3 py-2 text-right font-semibold">
                                {t("guardian.status")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detail.data?.payments || []).map((p) => (
                              <tr
                                key={p.id}
                                className="border-t border-[var(--subtle)]"
                              >
                                <td className="px-3 py-2">
                                  {formatDate(p.received_at || p.created_at)}
                                </td>
                                <td className="px-3 py-2 font-mono-num">
                                  {p.receipt_no || "—"}
                                </td>
                                <td className="px-3 py-2 capitalize">
                                  {localizedValue(
                                    "methods",
                                    p.method || "online",
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right font-mono-num font-semibold">
                                  {formatMoney(p.amount_paise)}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <Badge
                                    className={`${toneClass(statusTone(p.status))} border-0 capitalize`}
                                  >
                                    {localizedValue("statuses", p.status)}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {invoices.length === 0 && (
          <Card className="border-[var(--subtle)]">
            <CardContent className="py-10 text-center text-[var(--text-secondary)]">
              <Receipt className="h-8 w-8 mx-auto mb-2 opacity-40" />
              {t("guardian.noInvoicesYet")}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
